import React, { useMemo } from 'react';
import { findParentNodeClosestToPos, posToDOMRect } from '@tiptap/core';
import {
    ControlledBubbleMenu,
    MenuButton,
    MenuControlsContainer,
    MenuDivider,
    useDebouncedFocus,
    useRichTextEditorContext,
} from 'mui-tiptap';
import {
    DeleteColumn,
    DeleteRow,
    InsertColumnLeft,
    InsertColumnRight,
    InsertRowBottom,
    InsertRowTop
} from 'mui-tiptap/icons';
import GridOffIcon from '@mui/icons-material/GridOff';
import { ts } from 'src/utils/translate';

export const GeneralInfoTableBubbleMenu = () => {
    const editor = useRichTextEditorContext();
    const isEditorFocused = useDebouncedFocus({ editor });

    const anchorEl = useMemo(() => editor ? {
        getBoundingClientRect: () => {
            const table = editor.isActive('table')
                ? findParentNodeClosestToPos(editor.state.selection.$anchor, (node) => node.type.name === 'table')
                : null;
            const tableElement = table
                ? (editor.view.nodeDOM(table.pos) as HTMLElement | null)?.querySelector('table')
                : null;
            if (tableElement) {
                return tableElement.getBoundingClientRect();
            }
            const { from, to } = editor.state.selection;
            return posToDOMRect(editor.view, from, to);
        },
    } : null, [editor]);

    if (!editor?.isEditable) {
        return null;
    }

    return (
        <ControlledBubbleMenu
            editor={editor}
            open={isEditorFocused && editor.isActive('table')}
            anchorEl={anchorEl}
            placement="top-start"
            fallbackPlacements={['bottom-start', 'top', 'bottom', 'top-end', 'bottom-end']}
        >
            <MenuControlsContainer>
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_insert_column_before', 'Insert column before')}
                    IconComponent={InsertColumnLeft}
                    disabled={!editor.can().addColumnBefore()}
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_insert_column_after', 'Insert column after')}
                    IconComponent={InsertColumnRight}
                    disabled={!editor.can().addColumnAfter()}
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_delete_column', 'Delete column')}
                    IconComponent={DeleteColumn}
                    disabled={!editor.can().deleteColumn()}
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                />
                <MenuDivider />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_insert_row_above', 'Insert row above')}
                    IconComponent={InsertRowTop}
                    disabled={!editor.can().addRowBefore()}
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_insert_row_below', 'Insert row below')}
                    IconComponent={InsertRowBottom}
                    disabled={!editor.can().addRowAfter()}
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_delete_row', 'Delete row')}
                    IconComponent={DeleteRow}
                    disabled={!editor.can().deleteRow()}
                    onClick={() => editor.chain().focus().deleteRow().run()}
                />
                <MenuDivider />
                <MenuButton
                    tooltipLabel={ts('project_settings_quote_table_delete_table', 'Delete table')}
                    IconComponent={GridOffIcon}
                    disabled={!editor.can().deleteTable()}
                    onClick={() => editor.chain().focus().deleteTable().run()}
                />
            </MenuControlsContainer>
        </ControlledBubbleMenu>
    );
};
